import dayjs from "dayjs";
import { CategoryTypes } from "../interfaces/enums";
import { CategoryWithId } from "../interfaces/ICategories";
import { Contest, ContestTimingChipColor, ContestTimingChipInfo } from "../interfaces/IContests";
import { PossibleFormFieldValues } from "../interfaces/IForms";

export const formatDate = (date: Date) => {
  return date instanceof Date ? dayjs(date).format("DD.MM.YYYY") : "";
};

export const formatDateTime = (date: Date) => {
  if (!date) return "";
  return new Date(date).toLocaleDateString("pl-PL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const daysUntil = (date: Date) => {
  const today = new Date();
  const diffTime = date.getTime() - today.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

export const calculateAge = (birthDate: Date) => {
  if (!birthDate) return 0;
  const today = new Date();
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }

  return age;
};

export const isDateEnded = (date: Date) => {
  const today = new Date();
  return new Date(date) < today;
};

export const getDaysLeftStatus = (endDate: Date) => {
  const daysLeft = daysUntil(endDate);

  if (daysLeft <= 7) {
    return { type: "urgent", days: daysLeft };
  } else if (daysLeft <= 30) {
    return { type: "warning", days: daysLeft };
  } else {
    return { type: "normal", days: daysLeft };
  }
};

/**
 * Status Chipa na liście turniejów na podstawie dat kalendarzowych od-do.
 * - przyszłość: liczba dni do startu
 * - dziś mieści się w zakresie od-do: "Dzisiaj"
 * - dziś po dacie do: "Zakończony"
 */
export const getContestTimingChipInfo = (
  startDate: Date | null | undefined,
  endDate: Date | null | undefined,
): ContestTimingChipInfo => {
  const today = dayjs().startOf("day");
  const start = startDate ? dayjs(startDate).startOf("day") : today;
  const end = endDate ? dayjs(endDate).startOf("day") : start;
  const daysToStart = start.diff(today, "day");

  if (today.isAfter(end)) {
    return {
      status: "ended",
      label: "Zakończone",
      color: "default",
      tooltip: "Zawody zakończone",
      daysToStart,
    };
  }

  if (!today.isBefore(start)) {
    return {
      status: "ongoing",
      label: "Dzisiaj",
      color: "success",
      tooltip: "Turniej trwa",
      daysToStart,
    };
  }

  const type = daysToStart <= 7 ? "urgent" : daysToStart <= 30 ? "warning" : "normal";
  const color: ContestTimingChipColor =
    type === "urgent" ? "error" : type === "warning" ? "warning" : "default";

  return {
    status: "upcoming",
    label: `${daysToStart} dni`,
    color,
    tooltip: `Zawody wystartują za ${daysToStart} dni`,
    daysToStart,
  };
};

export const calculateDateWithOffset = (
  date: Date | null,
  offset: number | null | undefined,
  type: "min" | "max",
): Date | null => {
  if (!date) return null;
  const actualOffset: number = (offset as number) ?? (0 as number); // Jeśli offset jest null/undefined, przyjmujemy 0
  const adjustedDate = new Date(date);
  const currentYear = adjustedDate.getFullYear(); // Pobieramy obecny rok z daty

  if (type === "min") {
    adjustedDate.setFullYear(currentYear - actualOffset); // Odejmujemy offset od obecnego roku
  } else if (type === "max") {
    adjustedDate.setFullYear(currentYear + actualOffset); // Dodajemy offset do obecnego roku
  }

  return adjustedDate;
};

/**
 * Zwraca efektywny zakres dat urodzenia dla kategorii,
 * używany tylko do filtrowania listy zawodników.
 *
 * - Labelki / opisy kategorii nadal prezentują bazowy przedział
 *   (minParticipantBirthDate / maxParticipantBirthDate).
 * - Globalne offsety z contest.settings (minBirthdayDateOffset / maxBirthdayDateOffset)
 *   są stosowane TYLKO dla kategorii typu OTHER.
 */
export const getEffectiveAgeRangeForCategory = (
  category: CategoryWithId,
  contestSettings?: Contest["settings"] | null,
): { minDate: Date | null; maxDate: Date | null } => {
  const baseMin = category.minParticipantBirthDate ?? null;
  const baseMax = category.maxParticipantBirthDate ?? null;

  const globalMinOffset = contestSettings?.minBirthdayDateOffset ?? 0;
  const globalMaxOffset = contestSettings?.maxBirthdayDateOffset ?? 0;

  const effectiveMinOffset = category.type === CategoryTypes.OTHER ? globalMinOffset : 0;
  const effectiveMaxOffset = category.type === CategoryTypes.OTHER || category.type === CategoryTypes.DUO || category.type === CategoryTypes.TRIO ? globalMaxOffset : 0;

  const minDate = calculateDateWithOffset(baseMin, effectiveMinOffset, "min");
  const maxDate = calculateDateWithOffset(baseMax, effectiveMaxOffset, "max");

  return { minDate, maxDate };
};

const normalizeValue = (value: PossibleFormFieldValues): string => {
  return value === null ? "" : (value as string);
};

// Walidatory z normalizacją wartości
export const validateDateFormat = (value: PossibleFormFieldValues): string | null => {
  const normalizedValue = normalizeValue(value);
  const date = new Date(normalizedValue);

  if (isNaN(date.getTime())) {
    return "Niepoprawny format daty";
  }

  const [year, month, day] = normalizedValue.split(".").map(Number);
  if (day > 31 || day < 1 || month > 12 || month < 1) {
    return "Data zawiera nieprawidłowy dzień lub miesiąc";
  }

  return null;
};

export const validateYear = (value: PossibleFormFieldValues): string | null => {
  const normalizedValue = normalizeValue(value);
  const minYear = new Date("1950-01-01").getFullYear();
  const inputYear = new Date(normalizedValue).getFullYear();
  const currentYear = new Date().getFullYear();

  if (inputYear > currentYear) {
    return "maxYear";
  } else if (inputYear < minYear) {
    return "minYear";
  } else {
    return null;
  }
};

/**
 * Generuje opis przedziału wiekowego dla kategorii na podstawie dat granicznych urodzin
 *
 * Dopuszczalne przedziały:
 * - 01.01.2020 - 31.12.2021 - przedział "od-do"
 * - do 31.12.2021 - przedział "do", tylko starsze niż
 * - od 01.01.2020 - przedział "od" tylko młodsze niż
 * - "null-null" - Brak ograniczeń wiekowych
 *
 * @param category - Kategoria z datami granicznymi urodzin
 * @returns String z opisem przedziału wiekowego
 */
export const getCategoryAgeRangeDescription = (category: CategoryWithId): string => {
  const { minParticipantBirthDate, maxParticipantBirthDate } = category;

  // Przypadek: brak ograniczeń wiekowych (null-null)
  if (!minParticipantBirthDate && !maxParticipantBirthDate) {
    return "Brak ograniczeń wiekowych w tej kategorii";
  }

  // Przypadek: przedział "od-do" (obie daty ustawione)
  if (minParticipantBirthDate && maxParticipantBirthDate) {
    const minDate = dayjs(minParticipantBirthDate).format("DD.MM.YYYY");
    const maxDate = dayjs(maxParticipantBirthDate).format("DD.MM.YYYY");
    return `Urodzeni: ${minDate} - ${maxDate}`;
  }

  // Przypadek: tylko "do" (tylko maxParticipantBirthDate ustawione)
  // maxParticipantBirthDate = najstarsza dozwolona data urodzenia
  if (!minParticipantBirthDate && maxParticipantBirthDate) {
    const maxDate = dayjs(maxParticipantBirthDate).format("DD.MM.YYYY");
    return `Urodzeni do ${maxDate} (starsi zawodnicy)`;
  }

  // Przypadek: tylko "od" (tylko minParticipantBirthDate ustawione)
  // minParticipantBirthDate = najmłodsza dozwolona data urodzenia
  if (minParticipantBirthDate && !maxParticipantBirthDate) {
    const minDate = dayjs(minParticipantBirthDate).format("DD.MM.YYYY");
    return `Urodzeni od ${minDate} (młodsi zawodnicy)`;
  }

  // Fallback (nie powinno się zdarzyć)
  return "Brak informacji o przedziale wiekowym";
};

/**
 * Krótka wersja opisu przedziału wiekowego (do wyświetlenia w kompaktowym widoku)
 */
export const getCategoryAgeRangeShortDescription = (category: CategoryWithId): string => {
  const { minParticipantBirthDate, maxParticipantBirthDate } = category;

  if (!minParticipantBirthDate && !maxParticipantBirthDate) {
    return "Bez ograniczeń";
  }

  if (minParticipantBirthDate && maxParticipantBirthDate) {
    const minDate = dayjs(minParticipantBirthDate).format("DD.MM.YYYY");
    const maxDate = dayjs(maxParticipantBirthDate).format("DD.MM.YYYY");
    return `${minDate} - ${maxDate}`;
  }

  if (!minParticipantBirthDate && maxParticipantBirthDate) {
    const maxDate = dayjs(maxParticipantBirthDate).format("DD.MM.YYYY");
    return `do ${maxDate}`;
  }

  if (minParticipantBirthDate && !maxParticipantBirthDate) {
    const minDate = dayjs(minParticipantBirthDate).format("DD.MM.YYYY");
    return `od ${minDate}`;
  }

  return "—";
};

/**
 * Konwertuje różne typy dat na obiekt Date
 * Obsługuje: Date, Firestore Timestamp, string, number
 */
export const convertToDate = (dateValue: any): Date => {
  if (!dateValue) {
    return new Date();
  }
  if (dateValue instanceof Date) {
    return dateValue;
  }
  // Firestore Timestamp
  if (dateValue && typeof dateValue.toDate === "function") {
    return dateValue.toDate();
  }
  // String lub number
  if (typeof dateValue === "string" || typeof dateValue === "number") {
    const date = new Date(dateValue);
    if (!isNaN(date.getTime())) {
      return date;
    }
  }
  // Fallback
  return new Date();
};

/**
 * Porównuje dwie daty tylko pod kątem roku, miesiąca i dnia (ignoruje czas)
 * Przydatne do porównywania dat urodzenia
 */
export const compareDatesOnly = (date1: Date, date2: Date): boolean => {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
};
